import { Component, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { SettoriService, SottoSettore } from 'src/app/services/settori/settori.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

@Component({
  selector: 'app-dialog-crea-cfg-settori',
  templateUrl: './dialog-crea-cfg-settori.component.html',
  styleUrls: ['./dialog-crea-cfg-settori.component.scss'],
  standalone: false, // Mantengo false come da tua config, ma importo i moduli necessari se fosse standalone
})
export class DialogCreaCfgSettoriComponent implements OnInit {
  // Iniezione servizi tramite inject() - Pattern Angular 19
  private settoriService = inject(SettoriService);
  private utilityService = inject(UtilityService);
  private dialogRef = inject(MatDialogRef<DialogCreaCfgSettoriComponent>);

  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  /**
   * Definizione del Form con tipi forti
   */
  public formSettori = new FormGroup({
    id: new FormControl<string | undefined>(undefined),
    titolo: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, UtilityService.blankValidator],
    }),
    generico: new FormControl<boolean>(false, { nonNullable: true }),
    descrizione: new FormControl<string>('', { nonNullable: true }),
    codiciAteco: new FormControl<string[] | undefined>([], {
      validators: [Validators.required],
    }),
    sottoSettori: new FormControl<SottoSettore[] | undefined>([]),
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { settore: any }) {}

  ngOnInit(): void {
    this.inizializzaDati();
  }

  get isAtecoRequired(): boolean {
    return this.formSettori.get('codiciAteco')?.hasValidator(Validators.required) || false;
  }

  /**
   * Popola il form se siamo in modalità modifica
   */
  private inizializzaDati(): void {
    if (this.data?.settore) {
      // patchValue è più sicuro e pulito del ciclo Object.keys
      this.formSettori.patchValue(this.data.settore);
      this.updateValidatoriForm();
    }
  }

  /**
   * Gestisce il cambio tra settore generico e specifico
   */
  public cambioRadio(): void {
    this.updateValidatoriForm();
  }

  /**
   * Aggiorna dinamicamente i validatori
   */
  public updateValidatoriForm(): void {
    const atecoCtrl = this.formSettori.controls.codiciAteco;
    const isGenerico = this.formSettori.controls.generico.value;

    if (isGenerico) {
      atecoCtrl.removeValidators(Validators.required);
    } else {
      atecoCtrl.addValidators(Validators.required);
    }
    atecoCtrl.updateValueAndValidity();
  }

  /**
   * Logica di validazione e pre-salvataggio
   */
  public salva(): void {
    // Se è generico, svuoto i codici Ateco
    if (this.formSettori.get('generico')?.value === true) {
      this.formSettori.controls.codiciAteco.setValue([]);
    }

    if (this.formSettori.invalid) {
      this.formSettori.markAllAsTouched();
      return;
    }

    const { generico } = this.formSettori.getRawValue();

    if (generico) {
      this.confermaSettoreGenerale();
    } else {
      this._eseguiSalvataggio();
    }
  }

  private confermaSettoreGenerale(): void {
    this.utilityService.openDialog({
      titolo: 'Attenzione',
      fontWeight: '500',
      descrizione: `
        <strong>Il settore è stato definito come generale.</strong><br>
        <ul>
          <li>Solo un settore può essere definito come generale.</li>
          <li>Eventuali altri settori generali saranno cambiati in non-generali.</li>
        </ul>
        <strong>Continuare?</strong>`,
      bottoni: [
        { nome_btn: 'Annulla', background_color: 'var(--mat-color-accent)' },
        {
          nome_btn: 'Continua',
          background_color: 'var(--mat-color-primary)',
          handler: () => this._eseguiSalvataggio(),
        },
      ],
    });
  }

  /**
   * Chiamata API finale
   */
  private _eseguiSalvataggio(): void {
    this.spinnerOver.show();
    const rawData = this.formSettori.getRawValue();
    const id = rawData.id;

    const saveObs = id
      ? this.settoriService.putSettori(id, rawData)
      : this.settoriService.postSettori(rawData);

    saveObs.subscribe({
      next: () => {
        this.spinnerOver.hide();
        this.utilityService.openSnackBar('Salvataggio effettuato', '', {
          duration: 2000,
          panelClass: ['success-snackbar'],
        });
        this.dialogRef.close(true);
      },
      error: () => {
        this.spinnerOver.hide();
        this.utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: 'Errore nel salvataggio',
          bottoni: [{ nome_btn: 'Chiudi' }],
        });
      },
    });
  }
}
